import { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import { get } from '../axios';
import { Role } from '../types/role';
import { useViewTenant } from '../contexts/ViewTenantContext';

interface WarningStatusProps {
  role: Role | null;
  apiKey: string;
  location: string;
}

const WARNING_STATUS_COOKIE = 'warning_status';
const COOKIE_EXPIRY = 5; // minutes

export const useWarningStatus = ({ role, apiKey, location }: WarningStatusProps) => {
  const [hasWarning, setHasWarning] = useState(false);
  const { viewTenantDomain } = useViewTenant();

  // Determine which endpoint to use based on current state
  const getEndpoint = useCallback(() => {
    if (viewTenantDomain) {
      return {
        endpoint: '/is-quota-exceeded',
        params: { viewTenantDomain }
      };
    }
    
    if (role === Role.MASTER) {
      return {
        endpoint: '/is-quota-exceeded-for-master',
        params: {}
      };
    }
    
    return {
      endpoint: '/is-quota-exceeded',
      params: {}
    };
  }, [viewTenantDomain, role]);

  const fetchWarningStatus = async () => {
    try {
      const { endpoint, params } = getEndpoint();
      const response = await get(endpoint, params, { apiKey });
      const status = response.data;
      setHasWarning(status);

      const cookieKey = `${WARNING_STATUS_COOKIE}_${role || 'default'}${viewTenantDomain ? `_${viewTenantDomain}` : ''}`;
      Cookies.set(cookieKey, JSON.stringify(status), {
        expires: COOKIE_EXPIRY / (24 * 60),
      });
    } catch (error) {
      console.error('Error fetching warning status:', error);
    }
  };

  useEffect(() => {
    if (role !== Role.TENANT && role !== Role.MASTER) {
      setHasWarning(false);
      return;
    }

    const cookieKey = `${WARNING_STATUS_COOKIE}_${role || 'default'}${viewTenantDomain ? `_${viewTenantDomain}` : ''}`;
    const cachedStatus = Cookies.get(cookieKey);

    if (!cachedStatus) {
      fetchWarningStatus();
      return;
    }

    try {
      const parsedStatus = JSON.parse(cachedStatus);
      setHasWarning(parsedStatus);
    } catch (error) {
      fetchWarningStatus();
    }
  }, [role, apiKey, location, viewTenantDomain]); // Dependencies include getEndpoint's dependencies

  return { hasWarning };
};
